import { Plus, ChevronsUpDown } from 'lucide-react'
import { Position, useNodeConnections, Handle, useNodeId } from '@xyflow/react'
import { FlowSourceTypes, type FlowTypes } from 'v2/types'
import { useState, useCallback } from 'react'
import { cn } from '@neo/lib/utils'
import { Tooltip } from '@neo/primitives/tooltip'
import {
  PortalToFollowElem,
  PortalToFollowElemContent,
  PortalToFollowElemTrigger,
} from '@neo/blocks/portal-to-follow'
import { useFlowStore } from '../store'
import { MenuItems } from '../panel'
import { FlowNodeTypes } from '../types'

interface StartNodeHandleProps {
  type: FlowTypes
  className?: string
}

export const StartNodeHandle = ({ type, className }: StartNodeHandleProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const isEditing = useFlowStore((state) => state.isEditing)

  const handleId = FlowSourceTypes[(type as FlowTypes) || 'incoming_call']
  const connections = useNodeConnections({
    handleType: 'source',
    handleId,
  })

  const isConnectable = connections.length < 1 // allow only one connection from the start node

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!isConnectable || !isEditing) return
      e.stopPropagation()
      setIsOpen((prev) => !prev)
    },
    [isConnectable, isEditing],
  )

  const id = useNodeId()

  const plusIconClasses = cn(
    'w-4 h-4 visible cursor-pointer dark:bg-secondary rounded-full p-[2px] stroke-background pointer-events-none !bg-none',
    {
      'text-tertiary': isConnectable,
      'text-secondary opacity-50': !isConnectable,
    },
  )

  return (
    <div className="absolute bottom-0 left-1/2 -translate-x-1/2">
      <PortalToFollowElem open={isOpen} onOpenChange={setIsOpen} placement="right" offset={20}>
        <PortalToFollowElemTrigger>
          <Tooltip
            content={
              connections.length > 0
                ? 'Start node can only connect to one node'
                : 'Click to add a new node'
            }
            className="w-48"
            delayDuration={1000}
          >
            <Handle
              type="source"
              position={Position.Bottom}
              isConnectable={isConnectable && isEditing}
              id={handleId}
              className={cn(
                '[&>*]:stroke-primary dark:[&>*]:stroke-background -bottom-2 left-1/2 -translate-x-1/2 size-4 bg-background border-1 border-st2 flex items-center justify-center',
                className,
              )}
              onClick={handleClick}
            >
              {isOpen ? (
                <ChevronsUpDown className={cn(plusIconClasses, '!visible')} />
              ) : (
                <Plus className={plusIconClasses} />
              )}
            </Handle>
          </Tooltip>
        </PortalToFollowElemTrigger>
        <PortalToFollowElemContent>
          <MenuItems
            onSelect={() => setIsOpen(false)}
            currentNodeType={FlowNodeTypes.start}
            sourceNodeId={id ?? undefined}
            open={isOpen}
            onOpenChange={setIsOpen}
          />
        </PortalToFollowElemContent>
      </PortalToFollowElem>
    </div>
  )
}
